// src/components/AboutUs.jsx - Redesigned Hero + Story (v2, with Scucci the Dog section)
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
// ----------------------------------------
// Reusable bits
// ----------------------------------------
const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur">
    {children}
  </span>
);
const CTAButton = ({ onClick, children, variant = 'primary', ...props }) => (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    type="button"
    className={
      variant === 'primary'
        ? 'px-6 py-3 rounded-2xl bg-white text-gray-900 text-sm font-semibold shadow hover:shadow-lg'
        : 'px-6 py-3 rounded-2xl border border-white/70 text-white text-sm font-semibold hover:bg-white/10'
    }
    {...props}
  >
    {children}
  </motion.button>
);

function ArrowRight() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12h14"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13 5l7 7-7 7"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Sticky mobile-only CTA (non-obnoxious)
const StickyMobileCTA = () => (
  <div className="fixed bottom-4 left-0 right-0 z-50 flex justify-center md:hidden pointer-events-none">
    <div className="pointer-events-auto bg-white/90 backdrop-blur border border-gray-200 shadow-xl rounded-full px-4 py-2 flex items-center gap-3">
      <span className="text-sm font-semibold text-gray-800">
        Try Scucci on your own photos
      </span>
      <a
        href="/register"
        aria-label="Start Free Trial"
        className="px-4 py-2 rounded-full bg-blue-900 text-white text-sm font-semibold hover:bg-blue-800 transition"
      >
        Start Free Trial
      </a>
    </div>
  </div>
);
const SectionHeader = ({ title, subtitle, id }) => (
<header id={id} className="text-center mb-12">
<h2 className="text-4xl md:text-5xl font-extrabold text-blue-900">{title}</h2>
{subtitle && (
<p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">{subtitle}</p>
    )}
</header>
);
const TimelineEvent = ({ title, description, image, alt, reverse = false, imageClassName = '' }) => {
const motionProps = {
initial: { opacity: 0, x: reverse ? 50 : -50 },
whileInView: { opacity: 1, x: 0 },
transition: { duration: 0.7 },
viewport: { once: true, amount: 0.3 },
  };
return (
<motion.div
{...motionProps}
className={`w-full mb-10 flex flex-col md:items-center ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'}`}
>
{/* Image (shows on mobile + desktop) */}
<div className="w-full md:w-5/12 h-64 md:h-80 bg-gray-100 rounded-2xl shadow-2xl overflow-hidden">
<img
src={image}
alt={alt}
className={`w-full h-full ${imageClassName || 'object-cover'}`}
loading="lazy"
/>
</div>
{/* Divider line (desktop only) */}
<div className="hidden md:block w-1/12">
<div className="w-px h-full bg-blue-300 mx-auto" />
</div>
{/* Copy */}
<div className="w-full md:w-6/12 mt-6 md:mt-0">
<div className="p-6 bg-white rounded-2xl shadow-xl border border-gray-100">
<h3 className="text-2xl font-bold text-blue-900 mb-3">{title}</h3>
<div className="text-gray-700 leading-relaxed">{description}</div>
</div>
</div>
</motion.div>
  );
};

const TrustStrip = () => (
  <section className="bg-white border-y">
    <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
      <div className="p-4 rounded-xl bg-gray-50 border">
        <p className="text-sm font-semibold text-blue-900">20+ Years on eBay</p>
        <p className="text-sm text-gray-600 mt-1">Hands-on reseller, understands the industry.</p>
      </div>
      <div className="p-4 rounded-xl bg-gray-50 border">
        <p className="text-sm font-semibold text-blue-900">University of Tampa</p>
        <p className="text-sm text-gray-600 mt-1">B.S. Sport Management · Varsity Baseball</p>
      </div>
      <div className="p-4 rounded-xl bg-gray-50 border">
        <p className="text-sm font-semibold text-blue-900">Patent‑Pending Platform</p>
        <p className="text-sm text-gray-600 mt-1">Built for reliability, not hype.</p>
      </div>
      <div className="p-4 rounded-xl bg-gray-50 border">
        <p className="text-sm font-semibold text-blue-900">Family‑Founded in Atlanta</p>
        <p className="text-sm text-gray-600 mt-1">Small team, big standards.</p>
      </div>
    </div>
  </section>
);
// ----------------------------------------
// MAIN COMPONENT
// ----------------------------------------
export default function AboutUs() {
const navigate = useNavigate();
const { user } = useAuth();
const isSignedIn = !!user;
const [isScrolled, setIsScrolled] = useState(false);
const trackNav = (to, placement) => {
  const suffix = placement ? `__${placement}` : '';
  trackEvent(`nav_about_to_${to}${suffix}`);
};
const handleStoryClick = (event) => {
  // Smooth scroll to mission anchor
  if (event) event.preventDefault();
  const element = document.getElementById('mission-personal');
  if (!element) return;
  const offset = 96;
  const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
  window.scrollTo({ top, behavior: 'smooth' });
};
useEffect(() => {
  const onScroll = () => {
    setIsScrolled(window.scrollY > 24);
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
  return () => window.removeEventListener('scroll', onScroll);
}, []);

const goPrimary = (placement) => {
  if (isSignedIn) return navigate('/estate-sales-pipeline');
  trackNav('register', placement);
  return navigate('/register');
};

const headerLabel = useMemo(() => {
  if (isSignedIn) return 'Go to Dashboard';
  return 'Get Started';
}, [isSignedIn]);

const headerCtaClass = useMemo(() => {
  const base =
    'inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-xl transition text-xs sm:text-sm';
  if (isScrolled) {
    return isSignedIn
      ? `${base} bg-blue-950 text-white font-extrabold shadow hover:shadow-md`
      : `${base} bg-blue-950 text-white font-semibold shadow hover:shadow-md`;
  }
  return isSignedIn
    ? `${base} bg-white text-blue-950 font-extrabold shadow hover:shadow-md`
    : `${base} bg-white/90 text-blue-950 font-semibold border border-white/40 hover:bg-white`;
}, [isScrolled, isSignedIn]);

return (
<div className="bg-gray-50 text-gray-800 font-sans">
      {/* Sticky header (match Welcome/Pricing) */}
      <header className="fixed top-0 inset-x-0 z-50">
        <div
          className={
            isScrolled
              ? 'absolute inset-0 bg-white/90 backdrop-blur border-b border-gray-200'
              : 'absolute inset-0 bg-blue-950/55 backdrop-blur'
          }
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                trackNav('home', 'header');
                navigate('/');
              }}
              className={
                isScrolled
                  ? 'text-blue-950 text-lg font-extrabold tracking-wider'
                  : 'text-white text-lg font-extrabold tracking-wider'
              }
              aria-label="Scucci Systems"
            >
              Scucci Systems
            </button>
            <span
              className={
                isScrolled
                  ? 'hidden md:inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-950 border border-blue-100'
                  : 'hidden md:inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white/90'
              }
            >
              Estate Sales
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                trackNav('home', 'header');
                navigate('/');
              }}
              className={
                isScrolled
                  ? 'px-3 sm:px-4 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-semibold rounded-xl hover:bg-gray-50 transition'
                  : 'px-3 sm:px-4 py-2 border border-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/10 transition'
              }
            >
              Home
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                trackNav('pricing', 'header');
                navigate('/pricing');
              }}
              className={
                isScrolled
                  ? 'px-3 sm:px-4 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-semibold rounded-xl hover:bg-gray-50 transition'
                  : 'px-3 sm:px-4 py-2 border border-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/10 transition'
              }
            >
              Pricing
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => goPrimary('header')}
              className={headerCtaClass}
            >
              {headerLabel}
              {isSignedIn ? <ArrowRight /> : null}
            </motion.button>

            {!isSignedIn && (
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/signin')}
                className={
                  isScrolled
                    ? 'px-4 sm:px-5 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-bold rounded-xl hover:bg-gray-50 transition'
                    : 'px-4 sm:px-5 py-2 border border-white/25 text-white text-xs sm:text-sm font-bold rounded-xl hover:bg-white/10 transition'
                }
              >
                Sign In
              </motion.button>
            )}
          </div>
        </div>
      </header>
{/* HERO - revised copy and consistency */}
<header className="relative min-h-screen flex items-center justify-center text-center text-white p-6 pt-24 md:pt-20 overflow-hidden">
<img
src="/images/about/cranework.jpg"
alt="Rigging truss at a live event, our zero-fail roots"
className="absolute inset-0 h-full w-full object-cover origin-right scale-[1.1] md:scale-[1.16]"
style={{ objectPosition: 'right top' }}
loading="eager"
/>
<div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80" />
<div
className="relative z-10 max-w-5xl"
style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.6)' }}
>
<div className="absolute -inset-x-6 -inset-y-8 rounded-3xl bg-gradient-to-b from-black/45 via-black/30 to-transparent blur-2xl pointer-events-none" />
<div className="relative">
<div className="mb-4 flex justify-center gap-2 scale-[1.07] origin-center">
<Badge>Patent‑pending</Badge>
<Badge>Family founded in Atlanta</Badge>
<Badge>20+ years on eBay</Badge>
</div>
<motion.h1
initial={{ opacity: 0, y: 20 }}
animate={{ opacity: 1, y: 0 }}
transition={{ duration: 0.8 }}
className="text-4xl md:text-6xl font-black tracking-tight leading-tight"
>
            Price your sales weeks in advance.
            Print clean, consistent tags fast.
</motion.h1>
<motion.div
initial={{ opacity: 0, y: 20 }}
animate={{ opacity: 1, y: 0 }}
transition={{ duration: 0.8, delay: 0.2 }}
className="mt-6 mx-auto max-w-3xl space-y-4 text-lg md:text-xl text-white/95"
>
<p>
  Scucci helps estate sale teams price earlier - from photos - and print professional tags in bulk, without changing how you run your sale.
</p>
<p>
  I&apos;m Joe Cutler, Co-Founder. My systems have to work the first time, because failure can be expensive. I learned that managing live events and rigging large-scale film productions.
</p>
<p>
  I grew up going to flea markets and thrift stores to help stock my mom&apos;s eBay store in the 1990s. Years later, when our family ran my mother&apos;s estate sale, I felt the same pain many operators do: slow pricing, manual entry, and handwritten tags.
</p>
<p>
  So I partnered with my dad, Keith - Co-Founder and CTO, a 30-year enterprise systems architect - to build Scucci: reliable tools that let you price items before the sale, build a draft inventory from photos, and print clean price tags in minutes.
</p>
</motion.div>
<div className="mt-8 flex flex-wrap justify-center gap-3">
  <CTAButton variant="primary" onClick={handleStoryClick} aria-label="Read our story">
    Read our story
  </CTAButton>
</div>
<div className="mt-8 mx-auto max-w-3xl">
  <div className="grid md:grid-cols-2 gap-4">
    <div className="rounded-2xl bg-white/10 border border-white/20 px-5 py-4 text-left">
      <div className="text-sm font-extrabold uppercase tracking-wide text-white">What Scucci is</div>
      <div className="mt-2 text-base text-white/95 leading-relaxed">
        Take photos. Scucci turns them into a draft inventory and printable price tags so you can price ahead of time and print faster from home or on site.
      </div>
    </div>
    <div className="rounded-2xl bg-white/10 border border-white/20 px-5 py-4 text-left">
      <div className="text-sm font-extrabold uppercase tracking-wide text-white">What Scucci is not</div>
      <div className="mt-2 text-base text-white/95 leading-relaxed">
        Not a point-of-sale system. Not an item researcher. Checkout can stay exactly the way you already run it.
      </div>
    </div>
  </div>
</div>
</div>
</div>
</header>
<main>
    <TrustStrip />
    {/* TRUST PROMISES - scan-friendly */}
    <section className="bg-white">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-blue-900">What we&apos;re building</h2>
          <p className="mt-3 text-lg text-gray-600 max-w-3xl mx-auto">
            Scucci is built to help you price in advance and print clear tags fast, without changing how you run checkout.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="text-sm font-extrabold uppercase tracking-wide text-blue-900">Plain language</div>
            <p className="mt-3 text-gray-700 leading-relaxed">
              We write everything for real operators. No jargon, no hidden catches, and no confusing workflows.
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="text-sm font-extrabold uppercase tracking-wide text-blue-900">Fits your workflow</div>
            <p className="mt-3 text-gray-700 leading-relaxed">
              Price at home, price on-site, print in bulk, or print on-demand. Scucci is built to adapt, not force a new process.
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="text-sm font-extrabold uppercase tracking-wide text-blue-900">Real support</div>
            <p className="mt-3 text-gray-700 leading-relaxed">
              When you reply to our emails or reach out, a real person answers. If something feels confusing, we fix it.
            </p>
          </div>
        </div>

        <div className="mt-10">
          <div className="w-full bg-blue-50 border border-blue-200 rounded-2xl px-6 py-5 text-center">
            <div className="text-blue-900 font-bold text-base">Questions?</div>
            <div className="text-gray-700 mt-2">
              Reply to <span className="font-semibold">support@scuccisystems.com</span>, and we&apos;ll help you get set up.
            </div>
          </div>
        </div>
      </div>
    </section>
{/* Pull-quote - tightened */}
<section className="py-16 px-6 bg-white border-y">
<div className="max-w-4xl mx-auto text-center">
<motion.blockquote
initial={{ opacity: 0 }}
whileInView={{ opacity: 1 }}
transition={{ duration: 0.8 }}
viewport={{ once: true }}
className="text-2xl md:text-3xl font-semibold text-blue-900"
>
&ldquo;Scucci is built with operators in mind, so you can print professional price tags faster, from home or on site, without all the manual data entry.&rdquo;
</motion.blockquote>
        <p className="mt-6 text-sm font-bold text-gray-700">- Joe Cutler, Co-Founder</p>
</div>
</section>
{/* WHY TEAMS TRUST SCUCCI - conversion-focused replacement */}
<section className="py-20 px-6 bg-white border-y">
  <div className="max-w-5xl mx-auto">
    <SectionHeader
      title="Why estate sale teams trust Scucci"
      subtitle="Built inside estate sales, not generic software."
    />

    <div className="grid md:grid-cols-3 gap-6">
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div className="text-sm font-extrabold uppercase tracking-wide text-blue-900">
          Built in the field
        </div>
        <p className="mt-3 text-gray-700 leading-relaxed">
          Scucci was designed while actively working estate sales, pricing, tagging, fixing mistakes, and dealing with real-world chaos.
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div className="text-sm font-extrabold uppercase tracking-wide text-blue-900">
          No workflow disruption
        </div>
        <p className="mt-3 text-gray-700 leading-relaxed">
          You do not have to change how you check out customers, manage staff, or run your sale. Scucci fits into what already works.
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div className="text-sm font-extrabold uppercase tracking-wide text-blue-900">
          Reliability over hype
        </div>
        <p className="mt-3 text-gray-700 leading-relaxed">
          The system is engineered for stability first, clear pricing, clean tags, and predictable results when timing matters.
        </p>
      </div>
    </div>

    <div className="mt-10 text-center">
      <p className="text-sm text-gray-600">
        For professional background and full work history, you can view Joe Cutler’s profile on LinkedIn.
      </p>
      <a
        href="https://www.linkedin.com/in/joekcutler"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block mt-3 text-blue-600 font-semibold hover:underline"
        aria-label="View Joe Cutler on LinkedIn"
      >
        View LinkedIn profile →
      </a>
    </div>
  </div>
</section>
{/* Personal mission */}
<section id="mission-personal" className="scroll-mt-24 py-20 px-6 bg-gray-100 border-y">
<div className="max-w-5xl mx-auto text-center">
<SectionHeader title="The Mission Becomes Personal" />
<motion.div
initial={{ opacity: 0 }}
whileInView={{ opacity: 1 }}
transition={{ duration: 0.8 }}
viewport={{ once: true }}
className="text-lg text-gray-700 leading-relaxed space-y-6 max-w-4xl mx-auto mb-12 text-left md:text-center"
>
<p>
                In 2023, our world was turned upside down. My mother and her longtime partner
                passed away unexpectedly from cancer within two weeks of each other. My brothers and I
                found ourselves navigating not only the emotional toll of being by her side in the hospital
                but the immediate logistical chaos that followed. 
</p>
<p>
                Our mother&apos;s collection of vintage glass, collectibles, art, antiques, and personally
                beaded jewelry spoke for itself. Growing up, we would hit flea markets and antique malls while
                on the road playing baseball. Her office was dedicated to selling on eBay in the late 90&apos;s and into the 2000s.
                Around 2016 she started her own estate sale business and sold furniture and collectibles at a local consignment shop.
                For years her collection grew as she had a direct line to hard to find items. More recently, we&apos;d
                hit thrift stores when I was in town for holidays and call to tell each other to talk about flips we had made.
                She always joked that her collection was our inheritance, but we never imagined it would become reality.
</p>
<p>
                When it did, I saw firsthand the industry&apos;s bottlenecks from the inside out. We curated her estate sale
                as best we could, and I experienced it all: the sheer impossibility of creating a detailed inventory, the
                frustration of manually tracking sales, the pain of theft, and the constant complaints about pricing and haggling. 
</p>
<p>
                That experience is ingrained into the soul of this company. It&apos;s the why behind every line of code. I&apos;m not just
                building a tool; I&apos;m building the solution I wish we had, that I hope empowers you. 
</p>
</motion.div>
<motion.div
initial={{ opacity: 0 }}
whileInView={{ opacity: 1 }}
transition={{ duration: 0.8, delay: 0.2 }}
viewport={{ once: true }}
className="max-w-4xl mx-auto"
>
<img
src="/images/about/family.jpg"
alt="Founder and family"
className="rounded-lg shadow-2xl object-cover w-full h-96"
loading="lazy"
/>
</motion.div>
</div>
</section>
{/* NEW: Why 'Scucci' - Scucci the Dog */}
<section className="py-20 px-6 bg-white border-b">
<div className="max-w-5xl mx-auto">
<SectionHeader
title={"Why 'Scucci' - Scucci the Dog"}
subtitle="Our name honors our family’s St. Bernard, the loyal guardian who kept watch during one of our toughest seasons."
/>
<motion.div
initial={{ opacity: 0, y: 20 }}
whileInView={{ opacity: 1, y: 0 }}
transition={{ duration: 0.8 }}
viewport={{ once: true }}
className="grid md:grid-cols-2 gap-10 items-center"
>
<div className="order-2 md:order-1 text-lg text-gray-700 leading-relaxed space-y-4">
<p>
                  Scucci™ is named after our family’s St. Bernard. During a time when our family
                  needed steadiness most, she was my mother’s protector and constant companion.
</p>
<p>
                  We chose her name for our company because it captures what we aspire to build:
                  systems that are loyal, dependable, and there when it matters.
</p>
</div>
<div className="order-1 md:order-2">
<img
src="/images/about/scuccithedog.jpg"
alt="Scucci the St. Bernard, the namesake of our company"
className="rounded-2xl shadow-2xl object-cover w-full h-96"
loading="lazy"
/>
</div>
</motion.div>
</div>
</section>
{/* Founders */}
<section className="py-20 px-6 bg-gray-100 border-y">
<SectionHeader
title="Meet the Architect"
subtitle="A 30-year veteran of enterprise systems architecture who built the Scucci engine."
/>
<div className="max-w-3xl mx-auto pt-8">
<motion.div
initial={{ opacity: 0 }}
whileInView={{ opacity: 1 }}
transition={{ duration: 0.8 }}
viewport={{ once: true }}
className="text-left"
>
<div className="w-full h-80 rounded-lg overflow-hidden shadow-lg mb-8 border-4 border-white">
<img
src="/images/about/architect.jpg"
alt="Keith Cutler, Co-Founder, CTO, and Architect"
className="w-full h-full object-cover"
loading="lazy"
/>
</div>
<div className="text-center mb-6">
<h3 className="text-3xl font-bold text-blue-900">Keith Cutler</h3>
<p className="text-xl text-gray-600">Co-Founder &amp; CTO</p>
</div>
<div className="text-gray-700 leading-relaxed space-y-4">
<p>
                  For over 30 years, Keith has been the expert major insurers call when their mission-critical
                  mainframe systems fail. He is a master of Cyberlife system architecture, one of the most complex
                  and robust administrative systems ever built.
</p>
<p>
                  His career has been defined by solving the deepest technical challenges: leading complex data
                  conversions, architecting release upgrades, and resolving the kinds of system-level problems
                  others can&apos;t. He doesn&apos;t just write code; he engineers industrial-grade systems designed
                  for decades of flawless operation. That&apos;s the reliability baked into the Scucci engine.
</p>
</div>
</motion.div>
</div>
</section>
{/* Word from the Founder */}
<section className="py-20 px-6 bg-white">
<div className="max-w-5xl mx-auto">
<SectionHeader title="A Word From the Co-Founder" />
<motion.div
initial={{ opacity: 0 }}
whileInView={{ opacity: 1 }}
transition={{ duration: 0.8 }}
viewport={{ once: true }}
className="grid md:grid-cols-3 gap-12 items-center"
>
<div className="md:col-span-1">
<img
src="/images/about/joec.jpeg"
alt="Joe Cutler, Co-Founder"
className="rounded-lg shadow-2xl object-cover w-full h-auto"
loading="lazy"
/>
</div>
<div className="md:col-span-2 text-lg text-gray-700 leading-relaxed space-y-4">
  <p>
    My passion for this business started in flea markets and antique malls as a kid with my parents and eventually grew into my own resale business. I love picking and uncovering artifacts of the past and preserving knowledge that would otherwise be lost.
  </p>
  <p>
    I know firsthand the hours of manual labor, research, and data entry that get in the way of growth. I built Scucci to ease that pain for estate-sale teams, liquidators, and small businesses that have no easy way to generate professional price tags and CSVs without friction.
  </p>
  <p>
    Most importantly, Scucci is a tool built to complement your expertise, not replace it. It&apos;s the quiet partner that handles tedious data entry and basic research so you can do more of what you love.
  </p>
</div>
</motion.div>
</div>
</section>
{/* Final CTA */}
<section className="bg-blue-900 text-white py-20 px-6 text-center w-full mb-0">
<h2 className="text-4xl font-extrabold mb-4">Start with a free trial.</h2>
<p className="text-lg md:text-xl opacity-90 mb-8 max-w-2xl mx-auto">
  Start a free trial and see Scucci on your own photos. If you want to talk first, reply to support@scuccisystems.com.
</p>
<div className="flex flex-wrap gap-3 justify-center">
  <a
    href={isSignedIn ? "/estate-sales-pipeline" : "/register"}
    aria-label={isSignedIn ? "Go to Dashboard" : "Start Free Trial"}
    onClick={() => {
      if (!isSignedIn) trackNav('register', 'footer');
    }}
  >
    <CTAButton variant="primary">{isSignedIn ? "Go to Dashboard" : "Start Free Trial"}</CTAButton>
  </a>
  <a
    href="/pricing"
    aria-label="View Pricing"
    onClick={() => trackNav('pricing', 'footer')}
  >
    <CTAButton variant="secondary">View Pricing</CTAButton>
  </a>
</div>
</section>
<div className="bg-blue-900 h-px w-full" />
{!isSignedIn && <StickyMobileCTA />}
</main>

</div>
  );
}
